import * as THREE from 'three';

import { Targets } from './Targets';
import { createLinesMesh, createMesh } from '../../../Graphics/standardRenderer';
import { GeoObject } from '@data/geodata/legacy/GeoObject.js';
import { getColorScheme, getGlobalState } from '@gui/store/persistent/persistentStore.js';
import { processVertexAttributesAndIndices } from '@data/geodata/legacy/GeoObject';
import FVector from '@data/geometry/FVector.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { getViewer } from 'store/viewerStore';
import { GroundShader } from './GroundShader.js';
import { GroundRoadTextures } from './GroundRoadTextures.js';
import { GroundTargetTextures } from './GroundTargetTextures.js';
import { TERRAIN_LINE_KIND, type TerrainLineKind } from '@visualizer/types';

const getTargetFillColors = (colorScheme: ReturnType<typeof getColorScheme>) => [
    colorScheme.GROUND,
    colorScheme.LAKE,
    colorScheme.FOREST,
    colorScheme.CUSTOM_POLYGON,
    colorScheme.RIVER,
];

const getTargetStrokeColors = (colorScheme: ReturnType<typeof getColorScheme>) => [
    colorScheme.GROUND_STROKE,
    colorScheme.LAKE_STROKE,
    colorScheme.FOREST_STROKE,
    colorScheme.CUSTOM_POLYGON_STROKE,
    colorScheme.RIVER_STROKE,
];

/**
 * The `Ground` class represents a component responsible for creating and managing the ground mesh
 * in the Three.js-based web visualizer.
 */
export class Ground extends GeoObject {
    strokeColor: THREE.Color = new THREE.Color(getColorScheme().GROUND_STROKE);

    // Materials:
    groundMaterial?: THREE.MeshBasicMaterial;
    groundShadowMaterial?: THREE.MeshLambertMaterial;
    groundShaderMaterial?: THREE.MeshBasicMaterial;
    groundTargetLambertMaterial?: THREE.MeshLambertMaterial;
    groundRoadOverlayMaterial?: THREE.MeshBasicMaterial | THREE.MeshLambertMaterial;
    private groundTexturedMeshGroup: THREE.Group = new THREE.Group();

    // Textures for shaders:
    private groundTextureTextures = new GroundShader();
    private groundRoadTextures = new GroundRoadTextures();
    private groundTargetTextures = new GroundTargetTextures();

    points: FVector[] | undefined;
    internTris: Map<number, any> | undefined; // Map of triangles in the inner region
    midTris: Map<number, any> | undefined; // Map of triangles in the middle region
    extTris: Map<number, any> | undefined; // Map of triangles in the outer region

    zones: any[]; // Zones of the ground mesh, if any
    vertices: Float32Array | undefined;
    waterTris: any[] | undefined; // Water triangles, if any
    private pendingTarget: Targets | null | undefined = null;
    private pendingGroundPoints: FVector[] | undefined = undefined;
    private static readonly ENABLE_COMBINED_OVERLAY_LAMBERT_LIGHTING = true;

    constructor() {
        super();
        this.name = 'Ground';
        this.vertAttr = [GeoObject.createVertexAttributesSimple()];
        this.zones = [];

        let material = new THREE.MeshBasicMaterial({
            color: getColorScheme().GROUND,
            side: THREE.FrontSide,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
        });
        this.material.push(material);
    }

    isVisible() {
        return getGlobalState().context.showGround;
    }

    override updateColors() {
        const colorScheme = getColorScheme();

        // Update basic & shadow materials:
        this.groundMaterial?.color.set(colorScheme.GROUND);
        this.groundShadowMaterial?.color.set(colorScheme.GROUND);

        // Update shader materials (textures):
        this.groundShaderMaterial?.color.set(colorScheme.GROUND);
        this.groundTargetLambertMaterial?.color.set(colorScheme.GROUND);

        const syncOverlayUniforms = (material?: THREE.Material) => {
            const overlayUniforms = (material?.userData as any)?.overlayUniforms;
            if (!overlayUniforms) return;
            const ambientMode = getGlobalState().ambient.mode;
            const showStrokes = getGlobalState().ambient.showStrokes;
            overlayUniforms.showTargetOverlays.value = getGlobalState().context.showTargets ? 1 : 0;
            if (overlayUniforms.showRoadOverlays) overlayUniforms.showRoadOverlays.value = getGlobalState().context.showRoads ? 1 : 0;
            if (overlayUniforms.showAnalysisBorderOverlays)
                overlayUniforms.showAnalysisBorderOverlays.value = getGlobalState().context.showAnalysisBorder ? 1 : 0;
            if (overlayUniforms.showLegalBorderOverlays)
                overlayUniforms.showLegalBorderOverlays.value = getGlobalState().context.showLegalBorder ? 1 : 0;
            if (overlayUniforms.showBuildableBorderOverlays)
                overlayUniforms.showBuildableBorderOverlays.value = getGlobalState().context.showBuildableBorder ? 1 : 0;
            if (overlayUniforms.enableTargetEdgeAA)
                overlayUniforms.enableTargetEdgeAA.value = ambientMode !== 'render' && showStrokes ? 1 : 0;
            if (overlayUniforms.strokeColor) overlayUniforms.strokeColor.value.set(colorScheme.GROUND_STROKE);
            if (overlayUniforms.analysisBorderColor) overlayUniforms.analysisBorderColor.value.set(colorScheme.ANALYSIS_BORDER);
            if (overlayUniforms.legalBorderColor) overlayUniforms.legalBorderColor.value.set(colorScheme.LEGAL_BORDER);
            if (overlayUniforms.buildableBorderColor) overlayUniforms.buildableBorderColor.value.set(colorScheme.BUILDABLE_BORDER);
            const expected = getTargetFillColors(colorScheme);
            const colors = overlayUniforms.targetColors.value as THREE.Color[];
            if (!Array.isArray(colors) || colors.length < 5) {
                overlayUniforms.targetColors.value = expected.map((color) => new THREE.Color(color));
            } else {
                for (let i = 0; i < 5; i++) colors[i].set(expected[i]);
            }

            // Target Strokes:
            const expectedStroke = getTargetStrokeColors(colorScheme);
            const strokeColors = overlayUniforms.targetStrokeColors?.value as THREE.Color[] | undefined;
            if (!Array.isArray(strokeColors) || strokeColors.length < 5) {
                if (overlayUniforms.targetStrokeColors) {
                    overlayUniforms.targetStrokeColors.value = expectedStroke.map((color) => new THREE.Color(color));
                }
            } else {
                for (let i = 0; i < 5; i++) strokeColors[i].set(expectedStroke[i]);
            }
        };
        syncOverlayUniforms(this.groundShaderMaterial);
        syncOverlayUniforms(this.groundTargetLambertMaterial);
        syncOverlayUniforms(this.groundRoadOverlayMaterial);

        // Stroke meshes:
        for (let i = 0; i < this.strokesMesh.children.length; i++) {
            const child = this.strokesMesh.children[i];
            if (!(child instanceof THREE.LineSegments)) continue;

            (child.material as THREE.LineBasicMaterial).color.set(colorScheme.GROUND_STROKE);
        }
    }

    override toggleVisibility() {
        super.toggleVisibility();

        const { ambient, context } = getGlobalState();

        const isRenderMode = ambient.mode === 'render' || ambient.mode === 'hybrid';

        // Visibility:
        const isVisible = this.isVisible();
        const isFillVisible = isVisible;
        const isStrokeVisible = isVisible && ambient.showStrokes && !isRenderMode;
        this.trianglesMesh.visible = isFillVisible;
        this.strokesMesh.visible = isStrokeVisible;

        // Materials:
        const activeMaterial = isRenderMode ? this.groundShadowMaterial : this.groundMaterial;

        // Assign mesh visibility & terrain material:
        this.trianglesMesh.children.forEach((child) => {
            if (!(child instanceof THREE.Mesh)) return;

            child.visible = this.isVisible();
            if (activeMaterial) child.material = activeMaterial;

            (child.material as THREE.Material).needsUpdate = true;
        });

        if (this.selectedMesh) {
            this.selectedMesh.visible = this.isVisible();
        }

        const showRoadLikeOverlays =
            context.showRoads || context.showAnalysisBorder || context.showLegalBorder || context.showBuildableBorder;
        const showTargetOverlay = context.showTargets && (!!this.groundShaderMaterial || !!this.groundTargetLambertMaterial);
        this.groundTexturedMeshGroup.visible = isVisible && (showRoadLikeOverlays || showTargetOverlay);
    }

    override initMeshes(): { trianglesMesh: THREE.Group; strokesMesh: THREE.Group } {
        const scene: THREE.Scene = getViewer().scene;

        this.groundTexturedMeshGroup = new THREE.Group();
        this.groundTexturedMeshGroup.name = 'GroundRoadOverlayPass';
        scene.add(this.groundTexturedMeshGroup);

        // 1. Prepare materials.
        this.groundMaterial = undefined;
        this.groundShadowMaterial = undefined;

        // 1.1. Apply colors to basic and shadowed ground materials:
        const triangleColor = this.vertAttr[0]?.triangleColor ?? getColorScheme().GROUND;
        this.groundMaterial = new THREE.MeshBasicMaterial({
            color: triangleColor,
            side: THREE.FrontSide,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: 3,
            polygonOffsetUnits: 3,
        });
        this.groundShadowMaterial = new THREE.MeshLambertMaterial({
            color: triangleColor,
            side: THREE.FrontSide,
        });

        // 2. Build meshes for all ground regions (inner, middle, outer):
        for (let i = 0; i < this.vertAttr.length; i++) {
            const groundMesh = createMesh(this.vertAttr[i].position.data, [...this.vertAttr[i].indices], this.groundMaterial);
            groundMesh.castShadow = false;
            groundMesh.receiveShadow = true;
            this.trianglesMesh.add(groundMesh);
            this.strokesMesh.add(
                createLinesMesh(this.vertAttr[i].position.data, [...this.vertAttr[i].indices], this.vertAttr[i].strokeColor)
            );

            if (i == 0) {
                this.vertices = this.vertAttr[i].position.data;
                this.selectedMesh = this.createSingleTriMesh(this.vertAttr[i].position.data);
                scene.add(this.selectedMesh);
            }
        }

        // 3. Add roads and targets textures to the shader and update shader materials:
        this.groundTargetTextures.setTargets(this.pendingTarget, this.pendingGroundPoints);
        this.updateTerrainlineShader();

        return { trianglesMesh: this.trianglesMesh, strokesMesh: this.strokesMesh };
    }

    disableTerrain(): void {
        const scene: any = getViewer().scene;
        if (scene) scene.remove(this.trianglesMesh);
    }

    override dispose() {
        super.dispose();
        const scene: any = getViewer().scene;
        scene.remove(this.groundTexturedMeshGroup);
        this.groundTexturedMeshGroup.traverse((child) => {
            const mesh = child as THREE.Mesh;
            const material = (mesh as any).material;
            if (Array.isArray(material)) {
                material.forEach((mat) => mat?.dispose?.());
            } else {
                material?.dispose?.();
            }
        });
        this.groundTexturedMeshGroup.clear();
        this.groundShaderMaterial?.dispose();
        this.groundShaderMaterial = undefined;
        this.groundTargetLambertMaterial?.dispose();
        this.groundTargetLambertMaterial = undefined;
        this.groundRoadOverlayMaterial?.dispose();
        this.groundRoadOverlayMaterial = undefined;
        this.groundMaterial?.dispose();
        this.groundMaterial = undefined;
        this.groundShadowMaterial?.dispose();
        this.groundShadowMaterial = undefined;
        this.clearTerrainLines();
        this.groundRoadTextures.dispose();
        this.groundTargetTextures.dispose();
        scene.remove(this.selectedMesh);
        this.selectedMesh.clear();
    }

    initializeGround(target?: Targets | null) {
        const colorScheme = getColorScheme();

        for (let i = 0; i < 3; i++) {
            this.vertAttr[i] = GeoObject.createVertexAttributesSimple();
            this.vertAttr[i].triangleColor = colorScheme.GROUND;
            this.vertAttr[i].strokeColor = colorScheme.GROUND_STROKE;
        }

        processVertexAttributesAndIndices(this.vertAttr[0], this.points!, Array.from(this.internTris!.values()), false);
        processVertexAttributesAndIndices(this.vertAttr[1], this.points!, Array.from(this.midTris!.values()), false);
        processVertexAttributesAndIndices(this.vertAttr[2], this.points!, Array.from(this.extTris!.values()), false);

        this.pendingTarget = target;
        this.pendingGroundPoints = this.points;
        this.groundTargetTextures.setTargets(target, this.points);
        if (this.trianglesMesh.children.length > 0) this.updateTerrainlineShader();

        for (let i = 0; i < this.vertAttr.length; i++) {
            const triangleColor = this.vertAttr[i].triangleColor;
            const material = new THREE.MeshBasicMaterial({
                color: triangleColor,
                side: THREE.FrontSide,
                polygonOffset: true,
                depthTest: true,
                polygonOffsetFactor: 3,
                polygonOffsetUnits: 3,
            });
            this.material.push(material);

            const shadowMaterial = new THREE.MeshLambertMaterial({
                color: triangleColor,
                side: THREE.FrontSide,
            });
            this.shadowMaterial.push(shadowMaterial);
        }
    }

    getAndDelete(map: any, key: any) {
        const value = map.get(key);
        map.delete(key);
        return value;
    }

    /**
     * Creates a single triangle mesh of the ground, using the passed vertices.
     */
    createSingleTriMesh(vertices: any, meshColor = 0xe1e1e1): THREE.Mesh {
        // Create geometry:
        const indices = [];
        indices.push(0);
        indices.push(0);
        indices.push(0);

        const geometry = new THREE.BufferGeometry();
        geometry.setIndex(indices);
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));

        // If meshColor is not an array, just create one material and one mesh
        const material = new THREE.MeshBasicMaterial({
            color: meshColor,
            side: THREE.DoubleSide,
            polygonOffset: true,
            depthTest: true,
            polygonOffsetFactor: -3,
            polygonOffsetUnits: 0.1,
        });
        let mesh = new THREE.Mesh(geometry, material);
        mesh.name = 'ground_mesh?';

        // Add shadows:
        mesh.castShadow = false;
        mesh.receiveShadow = true;

        // Add mesh to the scene:
        return mesh;
    }

    // =============== PROJECTED LINES ON TERRAIN ===============

    public addTerrainLines(
        line: LineSegments2,
        lineHalfWidth: number = GroundRoadTextures.DEFAULT_HALF_WIDTH,
        lineColor?: number,
        lineKind: TerrainLineKind = TERRAIN_LINE_KIND.ROAD
    ) {
        this.groundRoadTextures.addLine(line, lineHalfWidth, lineColor, lineKind);
    }

    public clearTerrainLines() {
        this.groundRoadTextures.clearLines();
    }

    public updateTerrainlineShader() {
        // 1. Compute road textures from current line sources:
        this.groundRoadTextures.refreshDescriptor();
        const roadDescriptor = this.groundRoadTextures.getDescriptor();

        // 2. Compute target textures from current targets:
        const targetDescriptor = this.groundTargetTextures.getDescriptor();

        if (this.trianglesMesh.children.length === 0) {
            return;
        }

        // 3. Dispose previous shader materials (which might contain old state such as old turned on targets):
        this.groundShaderMaterial?.dispose();
        this.groundShaderMaterial = undefined;
        this.groundTargetLambertMaterial?.dispose();
        this.groundTargetLambertMaterial = undefined;
        this.groundRoadOverlayMaterial?.dispose();
        this.groundRoadOverlayMaterial = undefined;
        this.groundTexturedMeshGroup.clear();

        const context = getGlobalState().context;
        const ambientMode = getGlobalState().ambient.mode;
        const showStrokes = getGlobalState().ambient.showStrokes;
        const useTargetLambert = ambientMode === 'render' || ambientMode === 'hybrid';
        const enableTargetEdgeAA = ambientMode !== 'render' && showStrokes;

        // 4. Show target texture on top of the current active material:
        if (targetDescriptor && context.showTargets) {
            const colorScheme = getColorScheme();
            const targetMaterial = this.groundTextureTextures.createGroundShaderMaterial(
                useTargetLambert ? 'lambert' : 'basic',
                useTargetLambert
                    ? (this.groundShadowMaterial?.color.getHex() ?? colorScheme.GROUND)
                    : (this.groundMaterial?.color.getHex() ?? colorScheme.GROUND),
                colorScheme.GROUND_STROKE,
                colorScheme.ANALYSIS_BORDER,
                colorScheme.LEGAL_BORDER,
                colorScheme.BUILDABLE_BORDER,
                null,
                targetDescriptor,
                getTargetFillColors(colorScheme),
                getTargetStrokeColors(colorScheme),
                false,
                false,
                false,
                false,
                context.showTargets,
                enableTargetEdgeAA,
                Ground.ENABLE_COMBINED_OVERLAY_LAMBERT_LIGHTING,
                false
            ) as THREE.MeshBasicMaterial | THREE.MeshLambertMaterial;
            targetMaterial.transparent = true;
            targetMaterial.alphaToCoverage = false;
            targetMaterial.polygonOffset = true;
            targetMaterial.polygonOffsetFactor = -3;
            targetMaterial.polygonOffsetUnits = -3;
            targetMaterial.depthWrite = false;

            this.trianglesMesh.children.forEach((child) => {
                if (!(child instanceof THREE.Mesh)) return;
                const targetTriangleMesh = new THREE.Mesh(child.geometry, targetMaterial);
                targetTriangleMesh.castShadow = false;
                targetTriangleMesh.receiveShadow = false;
                targetTriangleMesh.renderOrder = 1;
                this.groundTexturedMeshGroup.add(targetTriangleMesh);
            });

            if (useTargetLambert) this.groundTargetLambertMaterial = targetMaterial as THREE.MeshLambertMaterial;
            else this.groundShaderMaterial = targetMaterial as THREE.MeshBasicMaterial;
        }

        // 5. Show roads texture on top of the current active material:
        if (roadDescriptor) {
            const colorScheme = getColorScheme();
            this.groundRoadOverlayMaterial = this.groundTextureTextures.createGroundShaderMaterial(
                useTargetLambert ? 'lambert' : 'basic',
                this.groundMaterial?.color.getHex() ?? colorScheme.GROUND,
                colorScheme.GROUND_STROKE,
                colorScheme.ANALYSIS_BORDER,
                colorScheme.LEGAL_BORDER,
                colorScheme.BUILDABLE_BORDER,
                roadDescriptor,
                null,
                getTargetFillColors(colorScheme),
                getTargetStrokeColors(colorScheme),
                context.showRoads,
                context.showAnalysisBorder,
                context.showLegalBorder,
                context.showBuildableBorder,
                false,
                enableTargetEdgeAA,
                Ground.ENABLE_COMBINED_OVERLAY_LAMBERT_LIGHTING,
                false
            ) as THREE.MeshBasicMaterial | THREE.MeshLambertMaterial;

            this.groundRoadOverlayMaterial.transparent = true;
            this.groundRoadOverlayMaterial.alphaToCoverage = true;
            this.groundRoadOverlayMaterial.polygonOffset = true;
            this.groundRoadOverlayMaterial.polygonOffsetFactor = -4;
            this.groundRoadOverlayMaterial.polygonOffsetUnits = -4;
            this.groundRoadOverlayMaterial.depthWrite = false;

            this.trianglesMesh.children.forEach((child) => {
                if (!(child instanceof THREE.Mesh) || !this.groundRoadOverlayMaterial) return;
                const roadTriangleMesh = new THREE.Mesh(child.geometry, this.groundRoadOverlayMaterial);
                roadTriangleMesh.castShadow = false;
                roadTriangleMesh.receiveShadow = false;
                roadTriangleMesh.renderOrder = 2;
                this.groundTexturedMeshGroup.add(roadTriangleMesh);
            });
        }

        // 5.2. Add roads meshes if needed:
        const activeMaterial = useTargetLambert ? this.groundShadowMaterial : this.groundMaterial;
        this.trianglesMesh.children.forEach((child) => {
            if (!(child instanceof THREE.Mesh) || !activeMaterial) return;
            child.material = activeMaterial;
            child.castShadow = false;
            child.receiveShadow = useTargetLambert;
        });

        this.updateColors();
        this.toggleVisibility();
    }
}
